import React, { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Message } from 'types';
import { loadConversation } from 'lib/supabase-db';
import ChatHeader from './ChatHeader';
import QuickActions from './QuickActions';
import MessageList from './MessageList';
import ChatInput from './ChatInput';

interface ChatContainerProps {
  onSendMessage: (message: string, conversationId: string | null) => Promise<void>;
  messages: Message[];
  isLoading: boolean;
  inputValue: string;
  setInputValue: (value: string) => void;
  conversationId: string | null;
  setConversationId: (id: string | null) => void;
  setMessages: (messages: Message[]) => void;
  isLoadingConversation: boolean;
  setIsLoadingConversation: (loading: boolean) => void;
  isStreaming?: boolean;
  streamingContent?: string;
}

const ChatContainer: React.FC<ChatContainerProps> = ({
  onSendMessage,
  messages,
  isLoading,
  inputValue,
  setInputValue,
  conversationId,
  setConversationId,
  setMessages,
  isLoadingConversation,
  setIsLoadingConversation,
  isStreaming = false,
  streamingContent = '',
}) => {
  const [searchParams] = useSearchParams();

  // Track if we're in the middle of creating a new conversation to prevent refetch
  const isCreatingConversation = useRef(false);

  const loadConversationData = async (conversationId: string) => {
    setIsLoadingConversation(true);
    try {
      const conversationMessages = await loadConversation(conversationId);
      setMessages(conversationMessages);
      setConversationId(conversationId);
    } catch (error) {
      console.error('Error loading conversation:', error);
    } finally {
      setIsLoadingConversation(false);
    }
  };

  const resetToDefaultState = () => {
    setConversationId(null);
    setMessages([]);
    setIsLoadingConversation(false);
    isCreatingConversation.current = false;
  };

  const handleQuickAction = (prompt: string) => {
    setInputValue(prompt);
  };

  const handleSendMessage = async (message: string) => {
    // Mark that we're creating a new conversation if there isn't one yet
    if (!conversationId) {
      isCreatingConversation.current = true;
    }
    await onSendMessage(message, conversationId);
  };

  // Load conversation from URL parameter
  useEffect(() => {
    const conversationParam = searchParams.get('conversation');

    // Case 1: No conversation in URL - reset to new chat
    if (!conversationParam) {
      // But DON'T reset if we're in the middle of creating a conversation
      if (!isCreatingConversation.current) {
        resetToDefaultState();
      }
      return;
    }

    // Case 2: Same conversation ID - already loaded, do nothing
    if (conversationParam === conversationId) {
      isCreatingConversation.current = false;
      return;
    }

    // Case 3: New conversation just created (URL updated but state hasn't caught up)
    // We have messages and are creating a conversation
    if (isCreatingConversation.current && messages.length > 0) {
      // Just update the conversationId to match the URL
      setConversationId(conversationParam);
      isCreatingConversation.current = false;
      return;
    }

    // Case 4: Switching to a different conversation - fetch it
    if (conversationParam !== conversationId) {
      isCreatingConversation.current = false;
      loadConversationData(conversationParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, conversationId, messages.length]);

  const hasMessages = messages.length > 0;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      {!hasMessages ? <ChatHeader /> : null}

      {/* Main Content Area */}
      <div className="flex-1 pb-32 overflow-y-auto">
        {isLoadingConversation ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-4"></div>
              <p className="text-gray-600 text-sm">Loading conversation...</p>
            </div>
          </div>
        ) : !hasMessages ? (
          <QuickActions onQuickAction={handleQuickAction} />
        ) : (
          <MessageList
            messages={messages}
            isLoading={isLoading}
            isStreaming={isStreaming}
            streamingContent={streamingContent}
          />
        )}
        {/* Message Input - Fixed at bottom */}
        <ChatInput
          inputValue={inputValue}
          setInputValue={setInputValue}
          onSendMessage={handleSendMessage}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
};

export default ChatContainer;
